import { ApiError } from "@allergy123/api-client";
import type { RuleIssue } from "@allergy123/contracts/allergens";
import { NavLink } from "react-router";

const LINKS = [
  ["/catalog/panels", "Tray panels"],
  ["/catalog/profiles", "Allergen profiles"],
  ["/catalog/antigens", "Antigens"],
  ["/catalog/products", "Products"],
  ["/catalog/recipes", "Mix recipes"],
  ["/catalog/region-defaults", "Region defaults"],
] as const;

export function CatalogHeader({ title, intro }: { title: string; intro: string }) {
  return (
    <div className="mb-8">
      <nav aria-label="Catalog" className="mb-4 flex flex-wrap gap-4 text-small">
        {LINKS.map(([to, label]) => (
          <NavLink key={to} to={to} className={({ isActive }) => (isActive ? "font-semibold text-anchor" : "text-ink-secondary")}>
            {label}
          </NavLink>
        ))}
      </nav>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">{title}</h1>
      <p className="max-w-[68ch] text-ink-secondary">{intro}</p>
    </div>
  );
}

const MESSAGES: Record<string, string> = {
  abbreviation_taken: "Another antigen already uses that abbreviation.",
  control_kind_mismatch: "A control needs a control kind, and only controls can have one.",
  code_taken: "That code is already used.",
  not_draft: "Only a draft can be changed. Start a new draft first.",
  publisher_required: "Publishing needs the catalog-publisher role.",
  publish_rules: "This version breaks the publish rules. Run the check and fix each issue first.",
  profile_incomplete: "A published allergen profile paired with the panel has no entry for some of the panel's antigens. Add the entries to the profile and publish it first.",
  antigen_in_use: "An antigen can't become or stop being a control, or change its control kind, while it is on a draft or published tray panel. Take it off those panels first: change the wells in a draft, then publish it.",
  preferred_taken: "That antigen already has a preferred product; make that one an acceptable substitute first.",
  version_conflict: "A newer version was published. Review it first.",
  unknown_antigen: "That antigen does not exist.",
  unknown_recipe: "Choose an active mix recipe for this antigen.",
  unknown_panel: "That tray panel no longer exists.",
  unknown_profile: "That allergen profile no longer exists.",
  wrong_family: "That version belongs to a different panel or profile. Reload the page and try again.",
  duplicate_position: "The same position was sent twice.",
  duplicate_component: "A component appears twice.",
  self_component: "A mix can't contain its own antigen.",
  not_found: "Not found — it may have been removed.",
  invalid_json: "The request couldn't be read. Reload the page and try again.",
  catalog_unavailable: "The catalog needs the database. Start the API with npm run api:dev:db.",
  mfa_required: "Turn on two-step verification to use the console.",
  location_not_in_organization: "One of the chosen locations belongs to another organization. Reload the page and choose again.",
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const listOf = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isRecord) : []);

/** What a refusal's body names: each incomplete profile and its missing antigens, or each panel version an antigen is on. */
function detailText(err: ApiError): string {
  if (err.code === "profile_incomplete") {
    const parts = listOf(err.details.profiles).map((p) => `${String(p.code)} has no entry for ${listOf(p.missing).map((m) => String(m.displayName)).join(", ")}`);
    return parts.length ? ` ${parts.join("; ")}.` : "";
  }
  if (err.code === "antigen_in_use") {
    const panels = listOf(err.details.panels).map((p) => `${String(p.code)} v${String(p.version)} (${String(p.status)})`);
    return panels.length ? ` It is on ${panels.join(", ")}.` : "";
  }
  return "";
}

export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const { antigenName, itemName } = err.details;
    if (err.code === "preferred_taken" && typeof antigenName === "string" && typeof itemName === "string") return `${antigenName} already has a preferred product (${itemName}); make that one an acceptable substitute first.`;
    if (err.code === "invalid_request") return err.field ? `Check the ${err.field} field.` : "Some of the information isn't valid. Check each field.";
    const base = MESSAGES[err.code] ?? err.code;
    const where = err.field?.startsWith("position.") ? ` (position ${err.field.slice("position.".length)})` : "";
    return `${base}${where}${detailText(err)}`;
  }
  return err instanceof Error ? err.message : "Something went wrong";
}

/** The rule issues a refused publish (`publish_rules`) carries, for the page's issue list; null for any other error. */
export function publishIssues(err: unknown): RuleIssue[] | null {
  return err instanceof ApiError && err.code === "publish_rules" && Array.isArray(err.details.issues) ? (err.details.issues as RuleIssue[]) : null;
}
